import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  UploadCloud, Loader2, ShieldQuestion, Hash, FileSearch, ShieldCheck, Lock, Check, FolderLock, AlertTriangle,
} from "lucide-react";
import { Panel, Btn, Overline } from "../ui";
import EvidenceCitationViewer from "../EvidenceCitationViewer";
import { sha256File } from "../../lib/crypto";
import { useI18n, pickLang } from "../../lib/i18n";

const RISE_FROM = { opacity: 0, y: 14 };
const RISE_TO = { opacity: 1, y: 0 };

const SUFFICIENCY = {
  SUPPORTED_DRAFT: "text-aegis-emerald border-aegis-emerald/40 bg-aegis-emerald/10",
  LIMITED_EVIDENCE: "text-aegis-amber border-aegis-amber/40 bg-aegis-amber/10",
  INSUFFICIENT: "text-aegis-red border-aegis-red/40 bg-aegis-red/10",
};

const SANDBOX_BG =
  "https://images.unsplash.com/photo-1541816139614-b1c0b6a13f5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBkYXRhJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODc1NjEyMDl8MA&ixlib=rb-4.1.0&q=85";

export default function IntakeSeal({ evidence, matters, matterId, setMatterId, clientHash, setClientHash, onAnalyze, onSeal }) {
  const { t, lang } = useI18n();
  const [loading, setLoading] = useState(false);
  const [hashing, setHashing] = useState(false);
  const [report, setReport] = useState(null);
  const [sealing, setSealing] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const matter = matters.find((m) => m.matter_id === matterId);

  const analyze = async (file) => {
    if (!file) return;
    setReport(null);
    setHashing(true);
    let local = "";
    try {
      local = await sha256File(file);
      setClientHash(local);
    } catch {
      setClientHash("");
    }
    setHashing(false);
    setLoading(true);
    const data = await onAnalyze(file, matterId);
    if (data) setReport(data);
    setLoading(false);
  };

  const seal = async () => {
    if (!report) return;
    setSealing(true);
    const res = await onSeal(report.evidence_id, matterId, clientHash || report.sha256);
    if (res) setReport((r) => ({ ...r, sealed: true, seal_block: res.seal_block ?? res.block?.index }));
    setSealing(false);
  };

  const match = report && clientHash ? clientHash.toLowerCase() === report.sha256.toLowerCase() : null;

  return (
    <div className="space-y-6" data-testid="screen-intake">
      <div>
        <Overline>{t("seal.overline")}</Overline>
        <h1 className="mt-1.5 font-heading text-3xl font-light tracking-tighter text-slate-100">{t("seal.title")}</h1>
        <p className="mt-2 text-sm text-slate-400">{t("seal.sub")}</p>
      </div>

      <div className="mt-6">
        <EvidenceCitationViewer />
      </div>

      <Panel icon={FolderLock} title={t("seal.matter")} subtitle={t("seal.matter.hint")} accent="purple" testid="matter-selector">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {matters.map((m) => {
            const active = m.matter_id === matterId;
            return (
              <button
                key={m.matter_id}
                onClick={() => setMatterId(m.matter_id)}
                data-testid={`matter-${m.matter_id}`}
                className={`rounded-xl border p-4 text-left transition-colors duration-200 ${
                  active ? "border-aegis-purple/50 bg-aegis-purple/[0.07]" : "border-edge raise hover:border-aegis-purple/30"
                }`}
              >
                <p className={`font-mono text-[11px] ${active ? "text-aegis-purple" : "text-slate-400"}`}>{m.matter_id}</p>
                <p className="mt-1.5 text-[13px] font-medium text-slate-100">{pickLang(m, "name", lang)}</p>
                <p className="mt-1 text-[11px] text-slate-400">{pickLang(m, "authority", lang)}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2 font-mono text-[10px]">
                  <span className="rounded border border-edge px-1.5 py-0.5 text-slate-400">
                    {t("seal.evidenceCount", { n: m.evidence_count, s: m.sealed_count })}
                  </span>
                  {m.legal_hold && (
                    <span className="rounded border border-aegis-amber/40 bg-aegis-amber/10 px-1.5 py-0.5 text-aegis-amber">
                      {t("seal.legalhold")}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </Panel>

      <Panel icon={FileSearch} title={t("seal.overline")} subtitle={t("seal.hint")} accent="cyan">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            analyze(e.dataTransfer.files?.[0]);
          }}
          onClick={() => inputRef.current?.click()}
          data-testid="forensics-dropzone"
          className={`relative cursor-pointer overflow-hidden rounded-xl border border-dashed p-10 text-center transition-colors duration-200 ${
            drag ? "border-aegis-cyan bg-aegis-cyan/5" : "border-edgeStrong hover:border-aegis-cyan/50"
          }`}
        >
          <div
            className="absolute inset-0 viz-scrim"
            style={{ backgroundImage: `url(${SANDBOX_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
          />
          <div className="relative flex flex-col items-center gap-3">
            {hashing || loading ? (
              <>
                <Loader2 size={30} className="animate-spin text-aegis-cyan" />
                <p className="text-sm text-slate-300">{hashing ? t("seal.hashing") : t("seal.analyzing")}</p>
              </>
            ) : (
              <>
                <UploadCloud size={30} className="text-aegis-cyan" />
                <p className="text-sm text-slate-200">{t("seal.drop")}</p>
                <p className="font-mono text-[11px] text-slate-400">{t("seal.hint")}</p>
              </>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            data-testid="forensics-file-input"
            onChange={(e) => analyze(e.target.files?.[0])}
          />
        </div>

        {clientHash && (
          <div className="mt-4 rounded-lg border border-edge well p-4" data-testid="client-hash-box">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Overline>{t("seal.clientHash")}</Overline>
              {match !== null && (
                <span
                  className={`flex items-center gap-1.5 rounded-md border px-2 py-0.5 font-mono text-[10px] ${
                    match
                      ? "border-aegis-emerald/40 bg-aegis-emerald/10 text-aegis-emerald"
                      : "border-aegis-red/40 bg-aegis-red/10 text-aegis-red"
                  }`}
                  data-testid="hash-match-badge"
                >
                  {match ? <Check size={11} /> : <AlertTriangle size={11} />}
                  {match ? t("seal.match") : t("seal.mismatch")}
                </span>
              )}
            </div>
            <p className="mt-2 break-all font-mono text-[11px] leading-relaxed text-aegis-cyan">{clientHash}</p>
          </div>
        )}
      </Panel>

      {report && (
        <motion.div initial={RISE_FROM} animate={RISE_TO}>
          <Panel
            icon={ShieldQuestion}
            title={`${t("seal.report")} — ${report.evidence_id}`}
            subtitle={`${report.filename} · ${matter?.matter_id || ""}`}
            accent="purple"
            testid="forensics-report"
            actions={
              <span className={`rounded-md border px-2.5 py-1 font-mono text-[11px] ${SUFFICIENCY[report.sufficiency] || SUFFICIENCY.LIMITED_EVIDENCE}`}>
                {report.sufficiency}
              </span>
            }
          >
            <div className="grid gap-5 lg:grid-cols-3">
              <div className="space-y-4 lg:col-span-2">
                <span className="inline-block rounded-md border border-aegis-purple/40 bg-aegis-purple/10 px-2.5 py-1 font-mono text-xs text-aegis-purple">
                  {report.classification}
                </span>
                <p className="text-sm leading-relaxed text-slate-300">{report.summary}</p>
                <div>
                  <Overline>{t("seal.chain")}</Overline>
                  <ul className="mt-2 space-y-2">
                    {report.evidence_chain?.map((c, i) => (
                      <li key={i} className="flex gap-2 text-[13px] text-slate-300">
                        <span className="font-mono text-aegis-purple">{String(i + 1).padStart(2, "0")}</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {report.recommendation && (
                  <div className="rounded-lg border border-aegis-cyan/25 bg-aegis-cyan/5 p-3 text-[13px] text-slate-200">
                    <span className="mr-2 font-mono text-[11px] uppercase text-aegis-cyan">{t("seal.recommend")}</span>
                    {report.recommendation}
                  </div>
                )}
                {report.sealed ? (
                  <div
                    className="flex items-center gap-2 rounded-lg border border-aegis-emerald/40 bg-aegis-emerald/10 px-4 py-3 text-sm text-aegis-emerald"
                    data-testid="evidence-sealed-banner"
                  >
                    <Lock size={16} /> {t("seal.sealed", { n: report.seal_block })}
                  </div>
                ) : (
                  <Btn variant="emerald" onClick={seal} disabled={sealing} testid="seal-evidence-btn" className="w-full py-3">
                    {sealing ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                    {sealing ? t("seal.sealing") : t("seal.btn")}
                  </Btn>
                )}
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border border-edge well p-4">
                  <Overline>{t("seal.threat")}</Overline>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="font-heading text-4xl font-light tabular text-aegis-red">{report.threat_score}</span>
                    <span className="text-xs text-slate-400">/ 100</span>
                  </div>
                  <div className="mt-2 h-1.5 w-full rounded-full track">
                    <div className="h-full rounded-full bg-aegis-red" style={{ width: `${Math.min(100, report.threat_score)}%` }} />
                  </div>
                </div>
                <div className="rounded-lg border border-edge well p-4">
                  <div className="flex items-center gap-1.5">
                    <Hash size={13} className="text-aegis-emerald" />
                    <Overline>{t("seal.digest")}</Overline>
                  </div>
                  <p className="mt-2 break-all font-mono text-[11px] leading-relaxed text-aegis-emerald">{report.sha256}</p>
                  <p className="mt-2 font-mono text-[10px] text-slate-400">
                    {report.size_bytes} bytes · .{report.file_type}
                  </p>
                </div>
              </div>
            </div>
          </Panel>
        </motion.div>
      )}

      <Panel icon={Lock} title={t("seal.list")} subtitle={`${evidence.length} artifacts`} accent="emerald" testid="evidence-list">
        {evidence.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">{t("seal.list.empty")}</p>
        ) : (
          <div className="space-y-2">
            {evidence.map((e) => (
              <div
                key={e.evidence_id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-edge raise px-4 py-3"
                data-testid={`evidence-row-${e.evidence_id}`}
              >
                <div className="min-w-0">
                  <p className="font-mono text-[12px] text-slate-100">
                    {e.evidence_id}
                    {e.matter_id && <span className="ml-2 text-slate-400">{e.matter_id}</span>}
                  </p>
                  <p className="truncate text-[12px] text-slate-400">
                    {e.filename} · {e.classification} · {e.threat_score}/100
                  </p>
                </div>
                {e.sealed ? (
                  <span className="flex items-center gap-1.5 font-mono text-[11px] text-aegis-emerald">
                    <Check size={13} /> #{e.seal_block}
                  </span>
                ) : (
                  <Btn variant="ghost" onClick={() => onSeal(e.evidence_id, e.matter_id || matterId, e.sha256)} testid={`seal-row-btn-${e.evidence_id}`}>
                    <ShieldCheck size={14} /> {t("seal.unsealed")}
                  </Btn>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
